/**
 * ====================================================================================
 * ARQUIVO: src/services/firestoreInterceptor.js
 * DESCRIÇÃO: Interceptor Global Seguro do Firestore SDK
 * 
 * PROTEÇÕES IMPLEMENTADAS:
 * 1. isPlainObject Check: Só percorre objetos literais ({}) e Arrays ([]).
 *    Instâncias do Firebase (Timestamp, DocumentReference, GeoPoint, Date, etc.)
 *    são preservadas instantaneamente sem travamento.
 * 2. Circular Reference Guard (WeakSet): Impossibilita qualquer loop infinito.
 * 3. Max Depth Guard (Limite de profundidade = 8).
 * 4. Fast-Bypass: Se a string não começar com 'enc:v1:', não aloca memória.
 * ====================================================================================
 */

import forge from 'node-forge';
import { DocumentSnapshot } from 'firebase/firestore';

const DEFAULT_KEY_HEX = 'a34347c8c878be630b9354115d1bdf43b6def97590cf352b1e4fc1cd4eb596ee';
const MASTER_KEY_HEX = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_PSM_ENCRYPTION_KEY)
    ? process.env.REACT_APP_PSM_ENCRYPTION_KEY.trim()
    : DEFAULT_KEY_HEX;

let keyBytes = null;
try {
    keyBytes = forge.util.hexToBytes(MASTER_KEY_HEX);
} catch (e) {
    console.warn('[FirestoreInterceptor] Erro ao converter chave mestra:', e);
}

/**
 * Decripta uma string cifrada (AES-256-GCM) de forma síncrona
 */
export function decryptTextSync(cipherText) {
    if (typeof cipherText !== 'string' || !cipherText.startsWith('enc:v1:') || !keyBytes) {
        return cipherText;
    }

    try {
        const parts = cipherText.split(':');
        if (parts.length !== 5) return cipherText;

        const [, , ivHex, tagHex, dataHex] = parts;
        const ivBytes = forge.util.hexToBytes(ivHex);
        const tagBytes = forge.util.hexToBytes(tagHex);
        const dataBytes = forge.util.hexToBytes(dataHex);

        const decipher = forge.cipher.createDecipher('AES-GCM', keyBytes);
        decipher.start({
            iv: ivBytes,
            tagLength: 128,
            tag: forge.util.createBuffer(tagBytes)
        });
        decipher.update(forge.util.createBuffer(dataBytes));
        const pass = decipher.finish();
        if (!pass) return cipherText;

        return forge.util.decodeUtf8(decipher.output.getBytes());
    } catch (e) {
        return cipherText;
    }
}

/**
 * Criptografa uma string com AES-256-GCM de forma síncrona
 */
export function encryptTextSync(plainText) {
    if (typeof plainText !== 'string' || plainText === '' || plainText.startsWith('enc:v1:') || !keyBytes) {
        return plainText;
    }

    try {
        const iv = forge.random.getBytesSync(12);
        const cipher = forge.cipher.createCipher('AES-GCM', keyBytes);
        cipher.start({
            iv: iv,
            tagLength: 128
        });
        cipher.update(forge.util.createBuffer(forge.util.encodeUtf8(plainText)));
        cipher.finish();

        const encrypted = cipher.output.toHex();
        const tag = cipher.tag.toHex();
        const ivHex = forge.util.bytesToHex(iv);

        return `enc:v1:${ivHex}:${tag}:${encrypted}`;
    } catch (e) {
        return plainText;
    }
}

function isPlainObject(obj) {
    if (!obj || typeof obj !== 'object') return false;
    const proto = Object.getPrototypeOf(obj);
    return proto === Object.prototype || proto === null;
}

/**
 * Percorre recursivamente objetos de forma segura com proteção contra circularidade
 */
export function decryptObjectRecursively(obj, seen = new WeakSet(), depth = 0) {
    if (!obj || typeof obj !== 'object') {
        if (typeof obj === 'string') return decryptTextSync(obj);
        return obj;
    }

    if (depth > 8) return obj;

    // Preservar instâncias especiais do Firebase (Timestamp, Date, DocumentReference, etc.)
    if (!isPlainObject(obj) && !Array.isArray(obj)) {
        return obj;
    }

    if (seen.has(obj)) return obj;
    seen.add(obj);

    if (Array.isArray(obj)) {
        return obj.map(item => decryptObjectRecursively(item, seen, depth + 1));
    }

    const decrypted = {};
    for (const [k, v] of Object.entries(obj)) {
        decrypted[k] = decryptObjectRecursively(v, seen, depth + 1);
    }
    return decrypted;
}

// Instalação do Hook Global no Firebase Firestore SDK
let isHookInstalled = false;

export function installFirestoreInterceptor() {
    if (isHookInstalled) return;

    try {
        if (DocumentSnapshot && DocumentSnapshot.prototype && typeof DocumentSnapshot.prototype.data === 'function') {
            const originalData = DocumentSnapshot.prototype.data;

            DocumentSnapshot.prototype.data = function (options) {
                const rawData = originalData.call(this, options);
                if (!rawData || typeof rawData !== 'object') return rawData;

                // Cache por snapshot
                if (this._psmDecryptedCache && this._psmRawRef === rawData) {
                    return this._psmDecryptedCache;
                }

                const decrypted = decryptObjectRecursively(rawData);
                this._psmDecryptedCache = decrypted;
                this._psmRawRef = rawData;
                return decrypted;
            };

            isHookInstalled = true;
            console.log('🛡️ [PSM Security] Interceptor Global Ativado com Proteção Anti-Trava.');
        }
    } catch (err) {
        console.warn('⚠️ [PSM Security] Falha ao instalar interceptor global:', err);
    }
}

// Ativa automaticamente
installFirestoreInterceptor();
